<template>
	<div v-if="member&&loaded" @contextmenu="showContextMenu">
		<CallDraggable :data="enhancedMember" :type="CallDragItemType.QueueMember" :target="false" :draggable="true">
			<div class="member-box">

				<div v-if="member.extension && employee?.type === PbxAppEmployeesType.EMPLOYEE" class="employee-picture-box">
					<PresenceIcon v-if="employee.type === PbxAppEmployeesType.EMPLOYEE" :state="employee.presence?.state as EmployeePresenceState" :size="16" class="presence-state" />
					<EmployeePicture :picture="employee.picture" :size="40" :spinner="true" :name="employee.name" />
				</div>

				<div v-if="member.extension && employee?.type === PbxAppEmployeesType.EXTENSION" class="external-name">{{ employee.primaryExtension?.number }}</div>

				<div v-if="!member.extension" class="external-name">{{ $t('queue.externalTag') }}</div>

				<div class="name-and-usage">
					<div v-if="employee?.type === PbxAppEmployeesType.EMPLOYEE" class="name dotdotdot flex">
						<div><GlyphIcon v-if="member.permanent" :icon="GlyphIconName['lock']" size="17" color="#aa0" style="float:left;margin-top:-2px" /></div>
						<div>{{ employee.name }}</div>
					</div>
					<div v-if="employee?.type !== PbxAppEmployeesType.EMPLOYEE && !member.extension" class="name dotdotdot">
						<GlyphIcon v-if="member.permanent" :icon="GlyphIcon['lock']" size="17" color="#aa0" style="float:left;margin-top:-2px" />
						{{ ss.cutNationalPrefix(member.number as string, true) }}
					</div>
					<div v-if="employee?.type === PbxAppEmployeesType.EXTENSION" class="name dotdotdot">
						<GlyphIcon v-if="member.permanent" :icon="GlyphIconName['lock']" size="17" color="#aa0" style="float:left;margin-top:-2px" />
						{{ employee.name }}
					</div>

					<div class="queues-container" v-tooltip.bottom="queueNamesLong">
						<span class="queue-names dotdotdot">{{ queueNamesShort }}</span>
					</div>

					<div class="usage-box">
						<div>
							<span class="usage-label">{{ $t('queue.memberPriority') }}</span>
							<span class="usage-value">{{ member.priority }}</span>
						</div>
						<div>
							<span class="usage-label">{{ $t('queue.memberCalls') }}</span>
							<span class="usage-value">{{ member.calls }}</span>
						</div>
						<div>
							<span class="usage-label">{{ $t('queue.memberTime') }}</span>
							<span class="usage-value" v-if="member.seconds">{{ duration(member.seconds, false, true) }}</span>
							<span class="usage-value" v-if="!member.seconds">0:00</span>
						</div>
					</div>

				</div>

				<div class="flex flex-col justify-center" v-if="employee">
					<div class="duration-box" :class="{'visible':employee&&employee.currentCall}">
						<div class="flex">
							<GlyphIcon
								:icon="employee.currentCallIcon"
								size="20"
								:color="employee.currentCallIconColor"
								class="m-0 shrink-0 text-center"
								:class="{ 'ringing': employee?.currentCall?.state === PbxCallState.CALL_RING, 'onhold': employee?.currentCall?.state === PbxCallState.CALL_HOLD }"
							/>
							<div style="margin-top:0px;margin-left:-3px">
								<glyph-icon v-if="currentCallIsAnsweredQueueCall" :icon="GlyphIconName['user-group']" color="var(--label-queue-color)" size="13" />
							</div>
						</div>

						<div :style="{ 'opacity': employee?.currentCall?.state === PbxCallState.CALL_RING ? 0 : 1 }" class="duration-time">{{ duration(currentCallDuration, true) }}</div>
					</div>
					<div class="duration-box" :class="{ 'visible': !(employee?.currentCall) && sinceLastCallEndedLabel !== '' && lastCallEndedToday }">
						<GlyphIcon :icon="GlyphIconName['call-idle']" size="30" color="#aaaaaa" style="margin: 0 auto;flex-shrink:0;margin-top:-4px" />
						<div class="text-center shrink-0" style="color:#aaaaaa;font-weight:500;margin-top:-15px;margin-left:2px">{{ sinceLastCallEndedTime }}<span style="font-size:11px;font-weight:300;padding-left:2px">{{ sinceLastCallEndedLabel }}</span></div>
					</div>
				</div>

			</div>
		</CallDraggable>
		<div class="bottom-line" :class="{'visible':!last}"></div>
		<ContextMenu ref="contextMenu" :model="contextMenuItems" :popup="true" />
	</div>
</template>

<script setup lang="ts">
import { useSettingsStore } from '@/stores/Settings'
import { useEmployeesStore } from '@/stores/Employees'
import { useApiStore } from '@/stores/Api'
import GlyphIcon from '@/components/GlyphIcon.vue'
import PresenceIcon from '@/components/PresenceIcon.vue'
import EmployeePicture from '@/components/EmployeePicture.vue'
import CallDraggable from '@/components/dashboard/CallDraggable.vue'
import globalEvents from '@/utilities/GlobalEvents'
import { useI18n } from 'vue-i18n'
import { PbxAppEmployeesType, type PbxAppQueuesMember } from '@/TcxApi/PbxApp'
import { computed, onBeforeUnmount, ref } from 'vue'
import type Employee from '@/stores/Employee'
import type ContextMenu from 'primevue/contextmenu'
import type { MenuItem } from 'primevue/menuitem'
import type { ErrorResponseDetail } from '@/TcxApi/ErrorResponse'
import { CallDragItemType } from '@/utilities/CallDrag'
import type { EmployeePresenceState } from '@per_moeller/tcx-realtime-client'
import { GlyphIconName } from '@/stores/GlyphIcons.types'
import { duration } from '@/utilities/filters'
import { PbxCallState } from '@/TcxApi/PbxCommon'

const ss = useSettingsStore()
const employees = useEmployeesStore()
const api = useApiStore()
const { t } = useI18n()

interface Props {
	member: PbxAppQueuesMember
	index: number
	queueIds: string[]
	queueNames: string[]
	last: boolean
}

const props = defineProps<Props>()

const canManage = ref(false)
const loaded = ref(false)
const employee = ref<Employee | null>(null)
const currentCallDuration = ref(0)
const sinceLastCallEndedTime = ref('')
const sinceLastCallEndedLabel = ref('')
const priorities = [ 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20 ]
const contextMenuItems: MenuItem[] = [
	{ label: t('queue.action.memberLeave'), command: leave },
	{
		label: t('queue.action.memberSetPriority'),
		items: priorities.map(pri => ({
			label: `${pri}`,
			icon: (pri === props.member.priority ? 'pi pi-check' : 'no-icon'),
			command: () => setPriority(pri)
		}))
	}
]

const contextMenu = ref<InstanceType<typeof ContextMenu>>()

const lastCallEndedToday = computed(() => {
	const today = new Date()

	today.setHours(0, 0, 0, 0)
	return props.member.lastCallEnded && props.member.lastCallEnded >= today
})

const enhancedMember = computed(() => {
	return { member: props.member, queueId: props.queueIds[0] || '' }
})

const currentCallIsAnsweredQueueCall = computed(() => {
	if (!employee.value?.currentCall) { return false }
	return employee.value.currentCall?.leg === 'O' && employee.value.currentCall.caller.queueId // && employee.value.currentCall.state === PbxCallState.CALL_ANSWER
})

const queueNamesShort = computed(() => {
	return props.queueNames.join(', ')
})

const queueNamesLong = computed(() => {
	return props.queueNames.join(', ')
})

function showContextMenu(event: MouseEvent) {
	event.preventDefault()
	if (canManage.value && !props.member.permanent) {
		contextMenu.value?.toggle(event)
	}
}

async function leave() {
	// Note: For multi-queue, we only remove from the first queue (this may need adjustment)
	try { await api.del(`/pbx/queue/${props.queueIds[0]}/member/${props.member._id}`) }
	catch (err) {
		if ((err as ErrorResponseDetail).status !== 404) {
			const name = employee.value?.name ?? props.member._id

			globalEvents.emit('CommonErrorModal', { header: t('queue.failedToRemoveMemberHeader'), message: t('failedToRemoveMemberMessage').replace('%%name%%', name) })
		}
	}
}

async function setPriority(priority: number) {
	// Note: For multi-queue, we only set priority on the first queue (this may need adjustment)
	try { await api.post(`/pbx/queue/${props.queueIds[0]}/member/${props.member._id}`, { priority: priority }) }
	catch (_) {
		const name = employee.value?.name ?? props.member._id

		globalEvents.emit('CommonErrorModal', { header: t('queue.failedToSetPriorityHeader'), message: t('failedToSetPriorityMessage').replace('%%name%%', name) })
	}
}

function refresh() {
	currentCallDuration.value = employee.value?.currentCallDuration ?? 0
	if (props.member.lastCallEnded) {
		const lce = typeof props.member.lastCallEnded === 'string' ? new Date(props.member.lastCallEnded) : props.member.lastCallEnded
		const value = Math.floor((new Date().getTime() - lce.getTime()) / 1000)
		const hours = Math.floor(value / 60 / 60)
		const minutes = Math.floor((value - (hours * 60 * 60)) / 60)
		const seconds = value - (hours * 60 * 60) - (minutes * 60)

		// | console.log(`${hours} - ${minutes} - ${seconds} - ${typeof this.member.lastCallEnded} - ${value}`);
		if (hours >= 24) {
			if (sinceLastCallEndedTime.value != '') {
				sinceLastCallEndedTime.value = ''
				sinceLastCallEndedLabel.value = ''
			}
		} else if (hours > 0) {
			sinceLastCallEndedTime.value = `${hours + Number((minutes / 60).toString().substring(0, 3))}`
			sinceLastCallEndedLabel.value = Number(sinceLastCallEndedTime.value) > 1 ? t('common.hours') : t('common.hour')
		} else if (minutes > 0) {
			sinceLastCallEndedTime.value = `${minutes}`
			sinceLastCallEndedLabel.value = t('common.minutes-short')
		} else {
			sinceLastCallEndedTime.value = `${seconds}`
			sinceLastCallEndedLabel.value = t('common.seconds-short')
		}
	} else if (sinceLastCallEndedTime.value !== '') {
		sinceLastCallEndedTime.value = ''
		sinceLastCallEndedLabel.value = ''
	}
}

async function initExtension() {
	if (!props.member.extension) { return }

	employee.value = await employees.getEmployeeByExtensionId(props.member.extension) as Employee

	if (employee.value) {
		currentCallDuration.value = employee.value?.currentCallDuration ?? 0
		loaded.value = true
	}
}

canManage.value = employees.me?.permissions.queueMember ?? false

if (props.member.extension) {
	initExtension()
} else {
	loaded.value = true
}

globalEvents.on('OneSecond', refresh)

onBeforeUnmount(() => {
	globalEvents.off('OneSecond', refresh)
})
</script>

<style scoped>
	.member-box {
		transition: 400ms;
		transition-delay: 1s;
		display: grid;
		grid-template-columns: 54px auto 70px;
		grid-template-rows: 50px;
		overflow: hidden;
		padding: 0;
		height: 50px;
	}
	.employee-picture-box {
		margin-left: 4px;
		margin-right: 4px;
		position: relative;
	}
	.presence-state {
		position: absolute;
		left: 30px;
		top: 24px;
		z-index: 1;
	}
	.employee-oncall {
		position: absolute;
		left: 30px;
		top: 0px;
		border: 1px solid var(--presence-busy);
		border-radius: 50%;
		z-index: 1;
		width: 16px;
		height: 16px;
		padding-top: -10px;
		background-color:var(--presence-busy);
		opacity: 0;
		transition: 1s;
	}
	.employee-oncall.active {
		opacity: 1;
	}
	.external-name {
		margin-left: 4px;
		margin-right: 4px;
		position: relative;
		background-color: var(--p-surface-200);
		color: var(--p-form-field-color);
		border: 1px solid var(--p-form-field-border-color);
		border-radius: 50%;
		text-transform: uppercase;
		font-weight: 400;
		text-align: center;
		font-size: 11px;
		display: flex;
		justify-content: center;
		align-items: center;
		width: 40px;
	}
	@media(prefers-color-scheme: dark) {
		.external-name {
			background-color: var(--p-surface-950);
			border: 1px solid #333;
			color: #BBAC91;
		}
	}
	.name-and-usage {
		display: grid;
		grid-template-rows: 16px 11px auto;
		/* align-content: center; */
	}
	.queues-container {
		font-size: 9px;
		line-height: 11px;
		color: var(--p-text-muted-color);
	}
	.queue-names {
		font-style: italic;
	}
	.usage-box {
		display: grid;
		grid-template-columns: 1fr 1fr 1fr;
		margin-top: 0px;
	}
	.usage-label {
		font-size: 10px;
		/* text-align: center; */
		/* font-weight: 400; */
		line-height: 12px;
		color: var(--p-text-muted-color);
		padding-bottom: 2px;
	}
	.usage-value {
		margin-left: 2px;
		font-size: 15px;
		line-height: 16px;
		color: #BBAC91;
		font-weight: 600;
		text-align: center;
	}

	.name {
		font-size: 14px;
		line-height: 16px;
		font-weight:500;
		letter-spacing: 0.01em;
		color: var(--p-text-color);
		margin-top: 2px;
	}
	.bottom-line {
		margin-left: 52px;
		margin-top: 2px;
		margin-bottom: 2px;
		border-bottom: 1px solid var(--p-form-field-border-color);
		opacity: 0;
		transition: 600ms;
		transition-delay: 200ms;
	}
	.bottom-line.visible {
		opacity: 0.5;
	}
	.duration-box {
		display: flex;
		flex-direction: column;
		justify-content: center;
		align-items: center;
		float: right;
		transition: 600ms;
		transition-delay: 300ms;
		overflow: hidden;
		margin-right: 3px;
		opacity: 0;
		max-height: 0px;
		min-height: 0px;
	}

	.duration-box.visible {
		opacity: 1;
		margin-top: 0px;
		max-height: 50px;
		min-height: 50px;
	}
	.duration-time {
		text-align: center;
		color: var(--p-text-muted-color);
		font-family:RobotoMonoThin;
		font-size:11px;
		flex-shrink:0;
		margin-top:0px;
		margin-left:2px;
	}
	.ringing {
		animation: shake 1s;
		animation-iteration-count: infinite;
		padding-top: 6px;
	}

	@keyframes shake {
		0% { transform: translate(2px, 0px); }
		10% { transform: translate(-2px, 1px); }
		20% { transform: translate(2px, -1px); }
		30% { transform: translate(-2px, 0px); }
		40% { transform: translate(2px, 0px); }
		50% { transform: translate(-2px, 0px); }
		60% { transform: translate(2px, 0px); }
		70% { transform: translate(-2px, 1px); }
		80% { transform: translate(2px, -1px); }
		90% { transform: translate(-2px, 0px); }
		100% { transform: translate(2px, 0px); }
	}
	.onhold {
		animation: holdanim 1s;
		animation-iteration-count: infinite;
	}
	@keyframes holdanim {
		0% { opacity: 1; }
		40% { opacity: 0.2; }
		70% { opacity: 1; }
	}
</style>
